import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { MeetingActionItem } from "@/lib/db-types";
import { canBecomeFollowUp, formatOffset } from "@/lib/meeting-summary-calc";

interface ActionItemRowProps {
  item: MeetingActionItem;
  owners: { id: string; full_name: string }[];
  disabled: boolean;
  /** Bare mulig når møtet har en kunde å knytte oppfølgingen til. */
  followUpsAvailable: boolean;
  onChange: (next: MeetingActionItem) => void;
  onJump: (offsetMs: number) => void;
}

/** Ett avtalt punkt: tekst, ansvarlig (Sølvrev-bruker), frist og «Legg til i oppfølging». Kundens punkter merkes «Kunden gjør». */
export default function ActionItemRow({ item, owners, disabled, followUpsAvailable, onChange, onJump }: ActionItemRowProps) {
  const ours = canBecomeFollowUp(item);
  return (
    <li className="space-y-2 border-[1.5px] border-border p-3 text-sm">
      <div className="flex flex-wrap items-start gap-2">
        <span className={ours ? "rounded-pill bg-muted px-2 py-0.5 text-xs font-medium" : "rounded-pill border border-border px-2 py-0.5 text-xs"}>
          {ours ? "Sølvrev gjør" : "Kunden gjør"}
        </span>
        {disabled ? (
          <p className="min-w-0 flex-1">{item.description}</p>
        ) : (
          <Input aria-label="Beskrivelse" className="h-8 min-w-0 flex-1 text-sm" maxLength={300} value={item.description} onChange={(e) => onChange({ ...item, description: e.target.value })} />
        )}
        {item.offset_ms !== null && (
          <button type="button" className="text-xs underline underline-offset-4" onClick={() => onJump(item.offset_ms!)} aria-label={`Gå til ${formatOffset(item.offset_ms)} i transkripsjonen`}>
            {formatOffset(item.offset_ms)}
          </button>
        )}
      </div>
      {ours ? (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={item.owner_user_id ?? "host"} onValueChange={(v) => onChange({ ...item, owner_user_id: v === "host" ? null : v })} disabled={disabled}>
            <SelectTrigger aria-label="Ansvarlig" className="h-8 w-48 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="host">Verten</SelectItem>
              {owners.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label="Frist"
            type="date"
            className="h-8 w-40 text-xs"
            value={item.due_date ?? ""}
            disabled={disabled}
            onChange={(e) => onChange({ ...item, due_date: e.target.value || null })}
          />
          {followUpsAvailable && (
            <Button
              type="button"
              size="sm"
              variant={item.add_follow_up ? "default" : "outline"}
              aria-pressed={!!item.add_follow_up}
              disabled={disabled}
              onClick={() => onChange({ ...item, add_follow_up: !item.add_follow_up })}
            >
              {item.add_follow_up ? "Legges i oppfølging" : "Legg til i oppfølging"}
            </Button>
          )}
        </div>
      ) : (
        item.owner_name && <p className="text-xs text-muted-foreground">Ansvarlig: {item.owner_name}</p>
      )}
    </li>
  );
}
